import { StorageKeys } from "../types/enums/Storage.enum";
import { storage } from "../utils/Const.utils";

export class HttpError extends Error {
    status: number;
    data?: any;
    isAuthError?: boolean;
    isNetworkError?: boolean;

    constructor(message: string, status: number, data?: any, flags?: { isAuthError?: boolean; isNetworkError?: boolean }) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
        this.data = data;
        this.isAuthError = flags?.isAuthError;
        this.isNetworkError = flags?.isNetworkError;
    }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type BodyType = "stringify" | "FormData"

interface Options {
    method?: HttpMethod;
    headers?: Record<string, string>;
    bodyType?: BodyType;
    body?: any;
    signal?: AbortSignal;
    skipAuth?: boolean; // <- Para requests que no necesitan autenticación
    _isRetry?: boolean; // <- Flag interno para evitar bucles infinitos
}

/**
 * Verificar si el token ha expirado
 */
export const isTokenExpired = (): boolean => {
    const expiresAt = storage.get(StorageKeys.EXPIRES_AT);
    if (!expiresAt) return true;
    
    try {
        const expirationTime = new Date(expiresAt).getTime();
        const currentTime = new Date().getTime();
        // Buffer reducido a 60 segundos para tokens de 15 minutos
        const bufferTime = 60 * 1000; // 60 segundos en milisegundos
        
        return (expirationTime - currentTime) <= bufferTime;
    } catch {
        return true;
    }
};

/**
 * Variable para evitar múltiples llamadas simultáneas a clearAuthData
 */
let isClearing = false;

/**
 * Variable para controlar el proceso de refresh token
 */
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

/**
 * Limpiar datos de autenticación
 * NOTA: Usar storage.clear() que preserva PERFIL para evitar pérdida durante
 * refrescos de token. El PERFIL solo se limpia en logout explícito.
 */
const clearAuthData = (): void => {
    /* Evitar múltiples limpiezas simultáneas */
    if (isClearing) return;
    
    isClearing = true;
    
    /* storage.clear() preserva el PERFIL automáticamente */
    storage.clear();
    
    //FIXME: Disparar evento personalizado para notificar el logout <-- Evaluar con anderson si dejar este comportamiento 
    window.dispatchEvent(new CustomEvent('auth:logout'));
    
    /* Resetear flag después de un breve delay */
    setTimeout(() => {
        isClearing = false;
    }, 100);
};

/**
 * Intentar refrescar el token de acceso con mejor protección contra race conditions
 */
const tryRefreshToken = async (): Promise<void> => {
    /* Si ya hay un refresh en progreso, esperar a que termine */
    if (isRefreshing && refreshPromise) {
        console.log('Refresh already in progress, waiting...');
        return refreshPromise;
    }

    /* Marcar que estamos refrescando */
    isRefreshing = true;
    
    refreshPromise = (async () => {
        try {
            /* Disparar evento para que AuthContext maneje el refresh */
            const event = new CustomEvent('auth:refresh-token-needed');
            window.dispatchEvent(event);
            
            /* Esperar tiempo suficiente para que el AuthContext actualice el token
               Aumentado a 500ms para manejar mejor latencia de red */
            await new Promise(resolve => setTimeout(resolve, 500));
            
            /* Verificar si el token fue actualizado */
            const newToken = storage.get(StorageKeys.ACCESS_TOKEN);
            const expiresAt = storage.get(StorageKeys.EXPIRES_AT);
            
            if (!newToken || !expiresAt) {
                throw new Error('Failed to refresh token');
            }
            
            console.log('Token refreshed successfully');
        } catch (error) {
            console.error('Error in tryRefreshToken:', error);
            throw error;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
        }
    })();
    
    return refreshPromise;
};

/**
 * Obtener token de acceso válido
 */
const getValidAccessToken = (): string | null => {
    const accessToken = storage.get(StorageKeys.ACCESS_TOKEN);
    
    if (!accessToken) return null;
    
    if (isTokenExpired()) {
        /* Token expirado, pero no limpiar inmediatamente - 
           dejar que el flujo de refresh lo maneje */
        return null;
    }
    
    return accessToken;
};
 
const CustomFetch = async( endpoint : string , options : Options ): Promise<any> => {
    const controller = new AbortController();
    options.signal = controller.signal;
    options.method = options.method || "GET";
    
    /* Construir headers por defecto */
    const defaultHeaders: Record<string, string> = {
        accept: "application/json",
    };

    /* Agregar Content-Type solo si no es FormData */
    if (options.bodyType !== "FormData") {
        defaultHeaders["content-type"] = "application/json";
    }

    /* Agregar Authorization si no se especifica skipAuth */
    if (!options.skipAuth) {
        const accessToken = getValidAccessToken();
        if (accessToken) {
            defaultHeaders["Authorization"] = `Bearer ${accessToken}`;
        } else {
            /* 
             * Endpoints públicos que NO requieren autenticación:
             * - login: Inicio de sesión
             * - register: Registro de nuevos usuarios v2
             * - RegistrarPerfil: Registro de personas físicas (ciudadanos/abogados)
             * - RegistrarPerfilEntidad: Registro de personas jurídicas
             * - VerificarUsuarioExiste: Verificar si usuario existe
             * - VerificarEntidadExiste: Verificar si entidad existe
             * - buscarCrm: Búsqueda en CRM para registro
             * - forgot-password: Recuperación de contraseña
             * - reset-password: Reseteo de contraseña
             * - ValidarTokenVU: Validación de token para VU
             * - GuardarValidacionVU: Guardado de resultados de VU
             */
            const publicEndpoints = [
                '/login',
                '/register',
                '/RegistrarPerfil',
                '/RegistrarPerfilEntidad',
                '/VerificarUsuarioExiste',
                '/VerificarEntidadExiste',
                '/buscarCrm',
                '/forgot-password',
                '/reset-password',
                '/ValidarTokenVU',
                '/GuardarValidacionVU',
                '/GenerarEnlacePruebaVida',
                '/GetAbogadosPorMatriculaCedula'
            ];
            
            const isPublicEndpoint = publicEndpoints.some(path => endpoint.includes(path));
            
            if (!isPublicEndpoint) {
                /* Si no hay token válido y no es endpoint público, limpiar y redirigir */
                clearAuthData();
                throw {
                    status: 401,
                    message: "Sesión expirada. Por favor, inicia sesión nuevamente.",
                    isAuthError: true
                };
            }
        }
    }

    /* Configurar headers y body */
    if(options.bodyType !== "FormData"){
        options.headers = { 
            ...defaultHeaders, 
            ...options.headers 
        };
        options.body = options.body ? JSON.stringify(options.body) : undefined;
        delete options.bodyType;
    } else {
        options.headers = { ...defaultHeaders, ...options.headers };
        delete options.headers["content-type"]; // <-- FormData establece su propio content-type
    }

    /* Eliminar body si está vacío */
    if (!options.body) delete options.body;

    try {
        const res = await fetch(endpoint, options);
        const data = await res.json().catch(() => null);

        if (!res.ok) {
            /* Manejar errores de autenticación */
            if (res.status === 401) {
                /* Si es un reintento o es una petición sin auth, no intentar refrescar */
                if (options._isRetry || options.skipAuth || endpoint.includes('/refresh')) {
                    clearAuthData();
                    throw new HttpError("Sesión expirada. Por favor, inicia sesión nuevamente.", res.status, undefined, { isAuthError: true });
                }

                /* Intentar refrescar el token y reintentar la petición */
                try {
                    await tryRefreshToken();
                    
                    /* Reintentar la petición original con el nuevo token */
                    const newAccessToken = storage.get(StorageKeys.ACCESS_TOKEN);
                    if (newAccessToken) {
                        /* Actualizar el header de autorización */
                        if (options.headers) {
                            options.headers["Authorization"] = `Bearer ${newAccessToken}`;
                        }
                        
                        /* Marcar como reintento para evitar bucles infinitos */
                        options._isRetry = true;
                        
                        /* Reintentar la petición */
                        return await CustomFetch(endpoint, options);
                    }
                } catch (refreshError) {
                    /* Si falla el refresh, limpiar y lanzar error */
                    clearAuthData();
                    throw new HttpError("Sesión expirada. Por favor, inicia sesión nuevamente.", 401, undefined, { isAuthError: true });
                }
            }

            if (res.status === 403) {
                throw {
                    status: res.status,
                    message: data?.message || "No tienes permisos para realizar esta acción.",
                    isAuthError: true,
                    data: data?.data
                };
            }

            /* Otros errores */
            throw new HttpError(data?.message || `Error ${res.status}: ${res.statusText}`, res.status, data);
        }

        return data;
    } catch (err: any) {
        /* Re-lanzar HttpError directamente (auth, 4xx, 5xx ya formateados) */
        if (err instanceof HttpError) {
            throw err;
        }

        /* Ignorar cancelaciones por navegación del usuario */
        if (err.name === 'AbortError' || (err instanceof DOMException && err.code === 20)) {
            return null as any;
        }

        /* Manejar errores de red (incluye "Load failed" en iOS Safari) */
        if (err instanceof TypeError) {
            throw new HttpError("Error de conexión. Verifica tu conexión a internet.", 0, undefined, { isNetworkError: true });
        }

        throw err;
    } finally {
        controller.abort();
    }
}

export const HTTP = () => {
    const Get = (url: string , options: Options = {}) => CustomFetch(url, { ...options, method: 'GET' });
    
    const Post = (url: string , options: Options = {}) => {
        return CustomFetch(url, { 
            ...options, 
            method: "POST",
            bodyType: "stringify" 
        });
    }
    
    const PostByFormData = (url: string , options: Options = {}) => {
        return CustomFetch(url, { 
            ...options, 
            method: "POST",
            bodyType: "FormData" 
        });
    }
    
    const Put = (url: string , options: Options = {}) => {
        return CustomFetch(url, { 
            ...options, 
            method: "PUT",
            bodyType: "stringify" 
        });
    }

    const Patch = (url: string , options: Options = {}) => {
        return CustomFetch(url, { 
            ...options, 
            method: "PATCH",
            bodyType: "stringify" 
        });
    }
    
    const Delete = (url: string , options: Options = {}) => {
        return CustomFetch(url, { 
            ...options, 
            method: "DELETE",
            bodyType: "stringify" 
        });
    }
    
    const SoftDel = (url: string , options: Options = {}) => {
        return CustomFetch(url, { 
            ...options, 
            method: "PUT",
            bodyType: "stringify" 
        });
    }
    
    return { Get, Post, PostByFormData, Put, Patch, Delete, SoftDel }
}